import { io } from "socket.io-client";
import { logger } from "@/helpers/loggerHelper";
import { SOCKET_URL_ADMIN } from "@/config/appConfig";

type SocketCustomId = string | number | null;
type SocketEvent = string;
type SocketPayload = unknown;
type SocketCallback = (_data?: SocketPayload) => void;

type SocketInstance = {
  connected: boolean;
  id?: string;
  io: {
    uri?: string;
    on: (_event: SocketEvent, _callback: (..._args: any[]) => void) => void;
  };
  on: (_event: SocketEvent, _callback: (..._args: any[]) => void) => void;
  off: (_event: SocketEvent, _callback: SocketCallback) => void;
  emit: (_event: SocketEvent, _data?: SocketPayload) => void;
  disconnect: () => void;
};

class MultiSocketService {
  private sockets: Map<string, SocketInstance>;

  constructor() {
    this.sockets = new Map();
  }

  _getKey(url: string, customId: SocketCustomId): string {
    return `${url}::${customId || ""}`;
  }

  connect(url = "", customId: SocketCustomId = null): SocketInstance | null {
    if (!url) return null;
    logger.info("Connecting socket with url:", url);
    const key = this._getKey(url, customId);
    const existingSocket = this.sockets.get(key);
    if (existingSocket?.connected) {
      logger.info("Socket already connected", key);
      return existingSocket;
    }
    const socket = io(url, {
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      auth: { customId },
      transports: ["websocket"],
    }) as unknown as SocketInstance;
    socket.on("connect", () => {
      logger.info(
        "\n __ Socket connected \nID",
        socket.id,
        " \nURL:",
        socket.io.uri
      );
    });
    socket.on("reconnect_attempt", (attempt: unknown) => {
      logger.info(`Reconnect attempt #${attempt}`);
    });
    socket.on("reconnect", () => {
      logger.info("Socket reconnected");
    });
    socket.on("disconnect", (reason: unknown) => {
      logger.info(`Socket disconnected: ${reason}`);
    });
    socket.on("error", (error: unknown) => {
      logger.error("Socket error:", error);
    });
    socket.io.on("transport", (transport: { name?: string }) => {
      logger.info("Transport being used:", transport.name);
    });
    this.sockets.set(key, socket);
    return socket;
  }

  disconnect(url = SOCKET_URL_ADMIN, customId: SocketCustomId = null): void {
    const key = this._getKey(url, customId);
    const socket = this.sockets.get(key);
    if (socket) {
      socket.disconnect();
      this.sockets.delete(key);
      logger.info("Socket manually disconnected", key);
    }
  }

  emit(
    url = SOCKET_URL_ADMIN,
    customId: SocketCustomId = null,
    event: SocketEvent,
    data?: SocketPayload
  ): void {
    const key = this._getKey(url, customId);
    const socket = this.sockets.get(key);
    if (socket && socket.connected) {
      socket.emit(event, data);
    } else {
      logger.warn("Cannot emit. Socket is not connected.", key);
    }
  }

  on(
    url = SOCKET_URL_ADMIN,
    customId: SocketCustomId = null,
    event: SocketEvent,
    callback: SocketCallback
  ): void {
    const key = this._getKey(url, customId);
    const socket = this.sockets.get(key);
    if (socket) socket.on(event, callback);
  }

  off(
    url = SOCKET_URL_ADMIN,
    customId: SocketCustomId = null,
    event: SocketEvent,
    callback: SocketCallback
  ): void {
    const key = this._getKey(url, customId);
    const socket = this.sockets.get(key);
    if (socket) socket.off(event, callback);
  }

  getInstance(
    url = SOCKET_URL_ADMIN,
    customId: SocketCustomId = null
  ): SocketInstance | undefined {
    const key = this._getKey(url, customId);
    return this.sockets.get(key);
  }
}

export const multiSocketService = new MultiSocketService();
